import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionButton } from 'src/routes/components/permission-button';
import { DisplayTextFields } from '../../perps-v1-shared/DisplayTextFields';
import { displayLabel } from '../../perps-v1-shared/api';
import { isValidCode, VIEW_CODE_MAX_LENGTH } from '../../perps-v1-shared/codeField';
import { errorMessage } from '../../perps-v1-shared/errorMessage';
import { PERPS_V1_VIEWS_PERMISSION } from '../../perps-v1-shared/constants';
import { MarketView } from '../../perps-v1-shared/types';
import { viewsApi } from '../dataProvider';
import { blankForm, blankFormWithParentRule, buildViewBody, formFromView, FormState } from '../viewForm';

interface ParentOption {
  code: string;
  label: string;
  depth: number;
}

interface Props {
  open: boolean;
  // Edit target; null in create mode (whether top-level or create-with-parent).
  view: MarketView | null;
  // Implied parent for "create as child of this row" (List.tsx's row-level "Add child view"
  // action). Ignored in edit mode. Null means create a top-level view.
  createParentView: MarketView | null;
  // displayOrder to seed a new view with — List.tsx computes max(sibling displayOrder) + 1 from
  // childrenByParent (top-level siblings for the page-level "New view" button, createParentView's
  // children for "Add child view"), so a new view always lands at the end of its group. There is
  // no longer a Display order input for the admin to edit this by hand — see viewForm.ts. Ignored
  // in edit mode, where the view's existing displayOrder is preserved untouched via formFromView.
  createDisplayOrder: number;
  parentOptions: ParentOption[];
  // parentCode -> 直接子 code 列表，用于排除被编辑视图自身及其整棵子树
  childrenCodes: Record<string, string[]>;
  onClose: () => void;
  // createdCode 只在「新建」保存成功时传入，供列表页顺势打开该视图的「管理币种」弹窗。
  onSaved: (createdCode?: string) => void;
}

// Basic navigation info only: code, label, badge, parent placement, display limit. Content &
// order (dexes/sectors/filters/include-exclude/sort/pinning/preview) live in ViewSymbolsDialog's
// "Manage Symbols" action — see viewForm.ts for why both dialogs share one full FormState and one
// buildViewBody even though this one only renders a subset of it.
//
// `displayOrder` is part of that shared FormState/buildViewBody (so saves never drop it) but has
// no input here: the List's ↑/↓ arrows are the only way to reorder existing siblings, and a new
// view's initial position comes from `createDisplayOrder` (List.tsx computes end-of-group), not
// from the admin typing a raw number.
export default function ViewEditorDialog({
  open,
  view,
  createParentView,
  createDisplayOrder,
  parentOptions,
  childrenCodes,
  onClose,
  onSaved,
}: Props) {
  const translate = useTranslate();
  const notify = useNotify();
  const isEdit = Boolean(view);

  const [form, setForm] = useState<FormState>(view ? formFromView(view) : blankForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (view) {
      setForm(formFromView(view));
    } else if (createParentView) {
      // Create-with-parent: pre-fill the child's rule from the parent (dexes/sectorCodes/sort/
      // filters) so there's nothing left for the admin to copy by hand — see blankFormWithParentRule.
      // displayOrder comes from the caller (end of this parent's children), not the parent itself.
      setForm(blankFormWithParentRule(createParentView, String(createDisplayOrder)));
    } else {
      // Page-level "New view" button: always a blank, top-level form — never inherits a rule
      // from whatever row was last used for "Add child view". displayOrder is the end of the
      // top-level group, from the caller.
      setForm(blankForm(String(createDisplayOrder)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view, createParentView, createDisplayOrder]);

  // 被编辑视图自身及其整棵子树不能作为自己的父级
  const excludedParentCodes = useMemo(() => {
    const set = new Set<string>();
    if (!view) return set;
    set.add(view.code);
    const walk = (code: string) => {
      (childrenCodes[code] ?? []).forEach((childCode) => {
        if (!set.has(childCode)) {
          set.add(childCode);
          walk(childCode);
        }
      });
    };
    walk(view.code);
    return set;
  }, [view, childrenCodes]);

  // 被编辑视图自身子树的高度：1=叶子，2=有子无孙，3=有孙。新建视图视为叶子。
  // 用于配合下方深度过滤，防止选中会令树超过三层的父级（后端会以 502 拒绝）。
  const subtreeHeight = useMemo(() => {
    if (!view) return 1;
    const children = childrenCodes[view.code] ?? [];
    if (children.length === 0) return 1;
    const hasGrandchildren = children.some(
      (childCode) => (childrenCodes[childCode] ?? []).length > 0
    );
    return hasGrandchildren ? 3 : 2;
  }, [view, childrenCodes]);

  // 父级候选：排除自身及其子树，并排除会使树超过三层的选项。
  // parentOptions 的 depth 为 0-based（顶层=0），后端规则按 1-based 层级校验
  // depth(parent) + subtreeHeight(edited) <= 3，换算为 0-based 即
  // p.depth + subtreeHeight <= 2。顶层（''）选项不受此限制，始终可选。
  const selectableParentOptions = useMemo(
    () =>
      parentOptions.filter(
        (p) => !excludedParentCodes.has(p.code) && p.depth + subtreeHeight <= 2
      ),
    [parentOptions, excludedParentCodes, subtreeHeight]
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Task ERRTOAST: the Code field used to accept anything and only fail server-side, with the
  // backend DTO's raw regex printed underneath as the admin's only clue. Check it here instead.
  // Create-mode only: the input is disabled when editing, and an existing view's code is not ours
  // to second-guess.
  const trimmedCode = form.code.trim();
  const codeInvalid =
    !isEdit && trimmedCode.length > 0 && !isValidCode(trimmedCode, VIEW_CODE_MAX_LENGTH);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = buildViewBody(form);

      if (isEdit && view) {
        await viewsApi.update(view.code, body);
        notify(translate('module.perpsV1Shared.saveSuccess'), { type: 'success' });
        onSaved();
      } else {
        await viewsApi.create(body);
        notify(translate('module.perpsV1Shared.saveSuccess'), { type: 'success' });
        // 新建视图后台默认是启用的，而这里只能填基本信息（规则/成员在「管理币种」里），
        // 所以把新建的 code 回传，让列表页紧接着打开管理弹窗，避免出现一个规则为空
        // （= 匹配全部币种）的标签页停留在线上。
        onSaved(form.code.trim());
      }
      onClose();
    } catch (error: any) {
      notify(
        errorMessage(error, translate('module.perpsV1Shared.saveError')),
        { type: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit
          ? translate('module.perpsV1Views.editView')
          : translate('module.perpsV1Views.newView')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={translate('module.perpsV1Views.fields.code')}
                value={form.code}
                disabled={isEdit}
                onChange={(e) => set('code', e.target.value)}
                error={codeInvalid}
                helperText={translate('module.perpsV1Shared.codeRule', {
                  max: VIEW_CODE_MAX_LENGTH,
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {isEdit ? (
                // Edit mode: re-parenting an existing view is still a thing — same Select,
                // same descendant/depth filtering as before.
                <TextField
                  fullWidth
                  select
                  size="small"
                  label={translate('module.perpsV1Views.fields.parent')}
                  value={form.parentCode}
                  onChange={(e) => set('parentCode', e.target.value)}
                >
                  <MenuItem value="">{translate('module.perpsV1Views.fields.parentNone')}</MenuItem>
                  {selectableParentOptions.map((p) => (
                    <MenuItem key={p.code} value={p.code} sx={{ pl: 2 + p.depth * 2 }}>
                      {p.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                // Create mode: the parent is implied by which row (if any) "Add child view" was
                // opened from, not chosen here — read-only text instead of a Select.
                <TextField
                  fullWidth
                  size="small"
                  label={translate('module.perpsV1Views.fields.parent')}
                  value={
                    createParentView
                      ? displayLabel(createParentView.label, createParentView.code)
                      : translate('module.perpsV1Views.fields.parentTopLevel')
                  }
                  disabled
                />
              )}
            </Grid>
          </Grid>

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {translate('module.perpsV1Views.fields.labelGroup')}
            </Typography>
            <DisplayTextFields
              value={form.label}
              onChange={(v) => set('label', v)}
              labelPrefix="module.perpsV1Shared.label"
            />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {translate('module.perpsV1Views.fields.badgeGroup')}
            </Typography>
            <DisplayTextFields
              value={form.badge}
              onChange={(v) => set('badge', v)}
              labelPrefix="module.perpsV1Shared.label"
              slugPlaceholder="auto"
            />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {translate('module.perpsV1Views.fields.displayLimitGroup')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={translate('module.perpsV1Views.fields.displayLimit')}
              value={form.displayLimit}
              onChange={(e) => set('displayLimit', e.target.value)}
              helperText={translate('module.perpsV1Views.fields.displayLimitHelper')}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={onClose} disabled={saving}>
          {translate('module.perpsV1Shared.cancel')}
        </LoadingButton>
        {/* Create mode POSTs /perps-v1/views (views:create); edit mode PUTs /perps-v1/views/:code
            (views:update) — the row-level "Add child view" entry point only checks :update. */}
        <PermissionButton
          resource={PERPS_V1_VIEWS_PERMISSION}
          action={isEdit ? 'update' : 'create'}
        >
          <LoadingButton
            variant="contained"
            onClick={handleSave}
            loading={saving}
            disabled={!trimmedCode || codeInvalid}
          >
            {translate('module.perpsV1Shared.save')}
          </LoadingButton>
        </PermissionButton>
      </DialogActions>
    </Dialog>
  );
}
